/**
 * ArticleCard — shared card primitive for "an article preview tile".
 *
 * Used by:
 *   - src/components/home/NewsTeaser.tsx (home page, 3 most recent)
 *   - src/pages/ArticleList.tsx          (/articles archive grid)
 *
 * History: the two were near-identical but had drifted in three places
 * (per A2 audit, 2026-10-09): NewsTeaser used `p-1` and ArticleList
 * `p-5`, only NewsTeaser showed `dateLabel`, and the `<Card>` `className`
 * for the inner padding was hand-set on each. This primitive is the
 * single source for both — drift now impossible because there is exactly
 * one rendering path.
 *
 * Layout contract:
 *   - `aspect-[16/10]` cover image with `object-cover` so cards stay
 *     uniform-height in the responsive grid (`md:grid-cols-3`); without
 *     the aspect ratio, a tall cover throws the row height.
 *   - Inner padding is driven by `<Card density="compact">` (14 / 20 px)
 *     which gives the excerpt + CTA room to breathe without shrinking
 *     the image to a thumbnail.
 *   - The `focus-visible` outline on the wrapping <Link> keeps the card
 *     keyboard-navigable; the parent Card may not inherit focus styles
 *     cleanly across browsers.
 *
 * Phase 1 mobile (2026-10-13): swapped the hand-set `p-5` for the new
 * `density="compact"` Card and replaced the inline `<h3 className=…>`
 * with the shared `<H3>` typography component (Phase 1 type scale).
 */
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Card } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import type { CmsArticleIndexEntry } from '@/content/cms/articles.generated'
import type { CmsMediaIndexEntry } from '@/content/cms/media.generated'
import type { Locale } from '@/admin/api'
import CmsImage from '@/components/media/CmsImage'
import { mediaAltForLocale } from '@/cms/mediaAlt'
import { uuidFromUploadPath } from '@/utils/mediaPath'

export interface ArticleCardProps {
  article: CmsArticleIndexEntry
  /** Resolved media entry for the article's cover, if any. Pass it
   *  pre-resolved so the parent controls the lookup semantics. */
  cover?: Pick<CmsMediaIndexEntry, 'urls' | 'alt'>
  /** Active locale — used for the published_at date format and the
   *  alt-text fallback chain. */
  locale: Locale
  /**
   * Visual variant.
   *
   * - `'full'` shows dateLabel and the explanatory excerpt (NewsTeaser).
   * - `'compact'` drops the date to make the /articles grid feel even
   *   (ArticleList).
   */
  variant?: 'full' | 'compact'
  /** "Read more" label key — tCms hookup. Pulled in via prop instead
   *  of an import so we don't pin this primitive to a specific i18n
   *  instance (avoids a useTranslation hook in a server-renderable
   *  component). */
  readMoreLabel: string
}

/**
 * The `sizes` for the card cover.
 *
 * Both callers lay the cards out in a `md:grid-cols-3` grid inside the
 * 1200px container, so a column is ≈33vw on a phone-to-desktop sweep and
 * ≈384px on a wide screen — where 33vw overstates it. Overstating is the
 * safe direction: a `sizes` that is too small makes the browser upscale.
 */
const COVER_SIZES = '(min-width: 768px) 33vw, 100vw'

/**
 * The media uuid behind a cover entry.
 *
 * The entry carries the file PATHS, not the uuid, but every media-library
 * file is named `<uuid>.<rung>.webp`, so the uuid can be recovered from the
 * name. It is the key into the media index, which is where the shared
 * <CmsImage> gets the srcset and the native size from.
 */
function coverUuid(
  cover: Pick<CmsMediaIndexEntry, 'urls'> | undefined,
): string | null {
  if (!cover) return null
  return (
    uuidFromUploadPath(cover.urls.original) ??
    uuidFromUploadPath(cover.urls.medium) ??
    uuidFromUploadPath(cover.urls.thumb)
  )
}

/** Pick the locale-appropriate title with the same fallback chain the
 *  server's `pickLocale` uses (server/routes/public.ts:411-438):
 *  current locale → zh-Hant → en. Keeps the card title in lockstep with
 *  the prerendered HTML and the live public API.
 *
 *  A trailing `|| ''` guards a stale generated index (pre-`_zhHant` shape),
 *  where the per-locale fields are all `undefined` and the card would
 *  otherwise crash on `.trim()`/`.slice()`. See NewsTeaser.tsx titleForLocale. */
function titleForLocale(
  article: CmsArticleIndexEntry,
  locale: Locale,
): string {
  if (locale === 'zh-Hant') return article.title_zhHant || article.title_en || article.title || ''
  if (locale === 'zh-Hans') {
    return article.title_zhHans || article.title_zhHant || article.title_en || article.title || ''
  }
  return article.title_en || article.title_zhHant || article.title || ''
}

/** Pick the locale-appropriate excerpt with the same chain. */
function excerptForLocale(
  article: CmsArticleIndexEntry,
  locale: Locale,
): string {
  if (locale === 'zh-Hant') return article.excerpt_zhHant || article.excerpt_en || article.excerpt || ''
  if (locale === 'zh-Hans') {
    return (
      article.excerpt_zhHans || article.excerpt_zhHant || article.excerpt_en || article.excerpt || ''
    )
  }
  return article.excerpt_en || article.excerpt_zhHant || article.excerpt || ''
}

export function ArticleCard({
  article,
  cover,
  locale,
  variant = 'full',
  readMoreLabel,
}: ArticleCardProps) {
  const coverMediaUuid = coverUuid(cover)
  const displayTitle = titleForLocale(article, locale)
  const displayExcerpt = excerptForLocale(article, locale)
  // The library's alt for the READER's script — an empty column counts as
  // "not filled in" and is skipped — falling back to the article's title.
  // (The old chain used `??`, which accepted an EMPTY alt and left the
  // image undescribed in English.)
  const coverAlt = mediaAltForLocale(cover, locale) || displayTitle
  const showDate = variant === 'full' && article.published_at
  const dateLabel = showDate
    ? new Date(article.published_at!).toLocaleDateString(
        locale === 'en' ? 'en-HK' : 'zh-HK',
        { year: 'numeric', month: 'long', day: 'numeric' },
      )
    : null

  return (
    <Link
      to={`/articles/${article.slug}`}
      className="block h-full focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-xl"
      aria-label={displayTitle}
    >
      <Card hover bordered density="compact" className="h-full overflow-hidden">
        {coverMediaUuid && (
          <CmsImage
            uuid={coverMediaUuid}
            alt={coverAlt}
            sizes={COVER_SIZES}
            // Always render at 16:10 so a grid of cards stays even-height
            // regardless of which article the operator picked.
            className="block w-full aspect-[16/10] object-cover"
          />
        )}
        <div>
          {dateLabel && <p className="text-xs text-ink-muted mb-1">{dateLabel}</p>}
          <H3 className="line-clamp-3 mb-2">
            {displayTitle || article.slug}
          </H3>
          {displayExcerpt && (
            <p className="text-sm text-ink-soft line-clamp-2 mb-3">{displayExcerpt}</p>
          )}
          <div className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
            <span>{readMoreLabel}</span>
            <Icon icon="lucide:arrow-right" />
          </div>
        </div>
      </Card>
    </Link>
  )
}

export default ArticleCard
